import { Cross2Icon } from "@radix-ui/react-icons";
import { Table } from "@tanstack/react-table";
import { useState } from "react";
import { toast } from "sonner";
import { AxiosError } from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";

import { Button } from "@/components/ui/button/button";
import { Input } from "@/components/ui/input/input";
import { DataTableViewOptions } from "./data-table-view-options";
import { DataTableFacetedFilter } from "./data-table-faceted-filter";
import { Lead } from "./schema";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover/popover";

import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command/command";
import { Label } from "@/components/ui/label/label";
import { deleteLeadsAsAdmin } from "@/config/mutation/admin/lead";

// import { DataTableFacetedFilter } from "./data-table-faceted-filter";

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
}

export function DataTableToolbar<TData>({
  table,
}: DataTableToolbarProps<TData>) {
  const isFiltered = table.getState().columnFilters.length > 0;

  const key = Cookies.get("key") as string;
  const queryClient = useQueryClient();

  const brokers = [
    {
      label: "Angel One",
      value: "ANGELONE",
    },
    {
      label: "Zerodha",
      value: "ZERODHA",
    },
    {
      label: "Alice Blue",
      value: "ALICEBLUE",
    },
    {
      label: "ICICI",
      value: "ICICI",
    },
    {
      label: "Upstox",
      value: "UPSTOX",
    },
  ];

  const [open, setOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterSelection, setFilterSelection] = useState({
    fNumber: 0,
    fStatus: "",
  });

  const handleOnChangeFilterSelection = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;

    setFilterSelection((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleSelectionOfFilter = () => {
    const rowModel = table.getCoreRowModel().rows;
    const filtered = rowModel.filter(
      (row) =>
        (row.original as Lead)?.status?.toLowerCase() ===
        filterSelection.fStatus?.toLowerCase()
    );

    let count = 0;

    for (const row of filtered) {
      if (row.getIsSelected()) {
        continue;
      }

      if (count < filterSelection.fNumber) {
        const originData = row.original as Lead;

        if (
          originData?.status?.toLowerCase() ===
          filterSelection.fStatus?.toLowerCase()
        ) {
          row.toggleSelected(true);
          count++;
        }
      }
    }

    if (count === 0) {
      toast("There is nothing to select.");
    } else {
      setFilterSelection({
        fNumber: 0,
        fStatus: "",
      });

      setIsFilterOpen(false);
    }
  };

  const { mutate, isPending } = useMutation({
    mutationFn: deleteLeadsAsAdmin(key),
  });

  const handleAssignSelectedLeads = () => {
    const selectedRows = table.getSelectedRowModel().rows;
    const formatData = selectedRows?.map((row) => {
      const originData = row.original as {
        id: string;
        alloted: boolean;
      };

      return originData?.id;
    });

    mutate(
      {
        leads: formatData,
      },
      {
        onSuccess: async () => {
          // console.log("New Employee", data);
          await queryClient.invalidateQueries({
            queryKey: ["leads"],
          });

          table.toggleAllPageRowsSelected(false);
          toast.success("Leads deleted.");
        },

        onError: (error) => {
          console.log("ERROR WHILE DELETING LEADS AS ADMIN", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  };

  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-1 items-center space-x-2">
        <Input
          placeholder="Filter by name..."
          value={(table.getColumn("name")?.getFilterValue() as string) ?? ""}
          onChange={(event) =>
            table.getColumn("name")?.setFilterValue(event.target.value)
          }
          className="h-8 w-[150px] lg:w-[250px]"
        />

        {table.getColumn("broker") && (
          <DataTableFacetedFilter
            column={table.getColumn("broker")}
            title="Broker"
            options={brokers}
          />
        )}

        <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
          <PopoverTrigger>
            <Button
              variant="outline"
              size="sm"
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              Selection Filter
            </Button>
          </PopoverTrigger>
          <PopoverContent className="max-w-xl w-full">
            <div className="flex flex-col gap-4">
              <div className="flex flex-row items-center gap-3">
                <div className="flex flex-col gap-1">
                  <Label htmlFor="f_number">How many selection?</Label>
                  <Input
                    id="f_number"
                    name="fNumber"
                    type="number"
                    value={filterSelection.fNumber}
                    onChange={handleOnChangeFilterSelection}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label htmlFor="f_broker">Which status?</Label>
                  <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex data-[state=open]:bg-muted"
                      >
                        {filterSelection.fStatus
                          ? filterSelection.fStatus
                          : "Status"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="mr-8">
                      <Command>
                        <CommandInput placeholder="search status..." />
                        <CommandList className="mt-2 [scrollbar-width:thin]">
                          <CommandEmpty>No results found.</CommandEmpty>
                          <>
                            {[
                              {
                                label: "Close",
                                value: "Close",
                              },
                              {
                                label: "Bank",
                                value: "Bank",
                              },
                              {
                                label: "Upload Document",
                                value: "Upload Document",
                              },
                              {
                                label: "Pan & DOB",
                                value: "Pan & DOB",
                              },
                              {
                                label: "Ringing",
                                value: "Ringing",
                              },
                              {
                                label: "Switch Off",
                                value: "Switch Off",
                              },
                              {
                                label: "Busy",
                                value: "Busy",
                              },
                              {
                                label: "Not Interested",
                                value: "Not Interested",
                              },
                              {
                                label: "Call Not Connected",
                                value: "Call Not Connected",
                              },
                              {
                                label: "Invalid No",
                                value: "Invalid No",
                              },
                              {
                                label: "Account open in other broker",
                                value: "Account open in other broker",
                              },
                              {
                                label: "Call disconnect",
                                value: "Call disconnect",
                              },
                              {
                                label: "He/She doing process",
                                value: "He/She doing process",
                              },
                              {
                                label: "Document Pending",
                                value: "Document Pending",
                              },
                              {
                                label: "Mobile Number not Linked",
                                value: "Mobile Number not Linked",
                              },
                              {
                                label: "Pan/Adhar Card Not Linked",
                                value: "Pan/Adhar Card Not Linked",
                              },
                              {
                                label: "Call back",
                                value: "Call back",
                              },
                              {
                                label: "Esign Count",
                                value: "Esign Count",
                              },
                              {
                                label: "Rejection",
                                value: "Rejection",
                              },
                              {
                                label: "Esign Done",
                                value: "Esign Done",
                              },
                              {
                                label: "Proceed from Another Number",
                                value: "Proceed from Another Number",
                              },
                            ].map((item) => {
                              return (
                                <CommandItem
                                  key={item.value}
                                  value={item.value}
                                  disabled={
                                    item.value === filterSelection.fStatus
                                  }
                                  onMouseDown={() => {
                                    setFilterSelection((prev) => {
                                      return {
                                        ...prev,
                                        fStatus: item.value,
                                      };
                                    });

                                    setOpen(false);
                                  }}
                                >
                                  {item.label}
                                </CommandItem>
                              );
                            })}
                          </>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="flex items-center justify-end">
                <Button
                  size="sm"
                  onClick={handleSelectionOfFilter}
                  className="bg-emerald-500 hover:bg-emerald-600"
                >
                  Select
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {table.getSelectedRowModel().rows.length > 0 && (
          <Button
            disabled={isPending}
            aria-label="Delete selected leads"
            size="sm"
            className="h-8 self-end"
            type="button"
            onClick={handleAssignSelectedLeads}
            variant="destructive"
          >
            Delete
          </Button>
        )}

        {isFiltered && (
          <Button
            variant="ghost"
            onClick={() => table.resetColumnFilters()}
            className="h-8 px-2 lg:px-3"
          >
            Reset
            <Cross2Icon className="ml-2 h-4 w-4" />
          </Button>
        )}
      </div>

      <DataTableViewOptions table={table} />
    </div>
  );
}
